import React, { useEffect, useState } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useChatStore } from '../../stores/chatStore';
import { aiAPI } from '../../utils/api';
import ChatHeader from './ChatHeader';
import ChatSessionList from './ChatSessionList';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';

const ChatWidget: React.FC = () => {
  const {
    isOpen,
    toggleChat,
    closeChat,
    sessions,
    activeSessionId,
    messages,
    isLoading,
    isStreaming,
    streamingContent,
    pendingConfirmation,
    showSessionList,
    toggleSessionList,
    loadSessions,
    selectSession,
    newSession,
    sendMessage,
    deleteSession,
    renameSession,
    confirmAction,
  } = useChatStore();

  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null);

  // Check AI status on mount and when widget opens
  useEffect(() => {
    if (isOpen) {
      loadSessions();
      aiAPI.getStatus()
        .then((res) => {
          const data = res.data as { ai_available?: boolean; ai_configured?: boolean; ai_enabled?: boolean };
          setAiAvailable(Boolean(data.ai_available && data.ai_configured && data.ai_enabled));
        })
        .catch(() => setAiAvailable(false));
    }
  }, [isOpen, loadSessions]);

  // Find the active session title
  const activeSession = sessions.find((s) => s.id === activeSessionId);

  // Don't render FAB if AI check failed permanently
  // (null = not checked yet, show FAB anyway)

  return (
    <>
      {/* FAB Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-6 right-6 z-50 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all duration-200 hover:scale-105"
          title="Assistente AI"
        >
          <ChatBubbleLeftRightIcon className="h-6 w-6" />
        </button>
      )}

      {/* Chat Panel */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[400px] h-[600px] max-h-[calc(100vh-48px)] bg-white rounded-xl shadow-2xl flex flex-col transition-all duration-300 origin-bottom-right ${
          isOpen
            ? 'opacity-100 scale-100 pointer-events-auto'
            : 'opacity-0 scale-95 pointer-events-none'
        }`}
      >
        <ChatHeader
          title={activeSession?.title || null}
          showSessionList={showSessionList}
          onToggleSessionList={toggleSessionList}
          onNewSession={newSession}
          onClose={closeChat}
        />

        {aiAvailable === false ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-1">Assistente AI non disponibile</p>
              <p className="text-xs text-gray-500">
                Configura le impostazioni AI nella sezione Impostazioni per attivare l'assistente.
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : showSessionList ? (
          <ChatSessionList
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={selectSession}
            onDelete={deleteSession}
            onRename={renameSession}
            onNewSession={newSession}
          />
        ) : (
          <ChatMessageList
            messages={messages}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
            pendingConfirmation={pendingConfirmation}
            onConfirm={confirmAction}
          />
        )}

        {!showSessionList && !isLoading && aiAvailable !== false && (
          <ChatInput onSend={sendMessage} disabled={isStreaming} />
        )}
      </div>
    </>
  );
};

export default ChatWidget;
